const KOMPAKTES_BILD_SEITENVERHAELTNIS = 3;

interface ITooltipBaseOptionen {
    id: string;
    cssKlasse: string;
    inhaltCssKlasse: string;
    ariaLabel: string;
    triggerSelektor: string;
    triggerAttribute: readonly string[];
}

/** Gemeinsame Dialog-, Trigger- und Positionierungslogik für umfangreiche Portal-Tooltips */
export default abstract class TooltipBase {
    protected readonly tooltip: HTMLDivElement; // gemeinsames Popover für alle Auslöser der Spezialisierung
    protected readonly content: HTMLDivElement; // austauschbarer Inhaltsbereich innerhalb des Popovers
    private activeTrigger: HTMLElement | null = null; // Auslöser, zu dem das Popover derzeit gehört

    protected constructor(private readonly optionen: ITooltipBaseOptionen) {
        this.tooltip = document.createElement('div');
        this.tooltip.id = optionen.id;
        this.tooltip.className = optionen.cssKlasse;
        this.tooltip.hidden = true;
        this.tooltip.setAttribute('role', 'dialog');
        this.tooltip.setAttribute('aria-label', optionen.ariaLabel);

        this.content = document.createElement('div');
        this.content.className = optionen.inhaltCssKlasse;
        this.tooltip.append(this.content);
        document.body.append(this.tooltip);

        this.initTriggers();
        this.initEvents();
    }

    /** Rendert den Inhalt des aktiven Auslösers und meldet ungültige Trigger zurück */
    protected abstract renderTrigger(trigger: HTMLElement): boolean;

    /** Verknüpft Klick, Tastatur, Medienereignisse und Fensterposition mit dem Popover */
    private initEvents(): void {
        document.addEventListener('click', event => {
            const trigger = this.getTrigger(event.target);
            if (trigger !== null) {
                this.toggle(trigger);
                return;
            }

            if (this.tooltip.contains(event.target as Node) === false)
                this.close();
        });

        document.addEventListener('keydown', event => {
            if (event.key === 'Escape' && this.activeTrigger !== null) {
                const trigger = this.activeTrigger;
                event.stopImmediatePropagation();
                this.close();
                trigger.focus();
                return;
            }

            const trigger = this.getTrigger(event.target);
            if (trigger === null || !['Enter', ' '].includes(event.key))
                return;

            event.preventDefault();
            this.toggle(trigger, true);
        });

        this.content.addEventListener('load', event => {
            if (!(event.target instanceof HTMLImageElement) || event.target.classList.contains('tooltip-aufgabenansicht-bild') === false)
                return;

            this.prepareAufgabenbild(event.target);
            this.lockContentHeight();
        }, true);

        this.content.addEventListener('error', event => {
            if (!(event.target instanceof HTMLImageElement) || event.target.classList.contains('tooltip-aufgabenansicht-bild') === false)
                return;

            const aufgabenansicht = event.target.closest<HTMLElement>('.tooltip-aufgabenansicht');
            aufgabenansicht?.querySelector<HTMLElement>('.tooltip-aufgabenansicht-bildbereich')?.setAttribute('hidden', '');

            const hinweis = aufgabenansicht?.querySelector<HTMLElement>('.tooltip-aufgabenansicht-hinweis') ?? null;
            if (hinweis !== null)
                hinweis.hidden = false;
            this.lockContentHeight();
        }, true);

        this.tooltip.addEventListener('toggle', () => this.position(), true);
        window.addEventListener('resize', () => this.lockContentHeight());
        window.addEventListener('scroll', () => this.position(), true);
    }

    /** Sucht ausgehend vom Ereignisziel den nächstgelegenen Auslöser dieser Spezialisierung */
    private getTrigger(target: EventTarget | null): HTMLElement | null {
        if (!(target instanceof Element))
            return null;

        return target.closest<HTMLElement>(this.optionen.triggerSelektor);
    }

    /** Rendert den Auslöserinhalt und zeigt das Popover an */
    private show(trigger: HTMLElement): boolean {
        if (trigger === this.activeTrigger && this.tooltip.hidden === false)
            return true;

        this.close();
        this.activeTrigger = trigger;
        this.content.style.height = '';
        if (this.renderTrigger(trigger) === false) {
            this.activeTrigger = null;
            return false;
        }

        this.tooltip.hidden = false;
        this.content.scrollTo(0, 0);
        requestAnimationFrame(() => this.content.scrollTo(0, 0));
        this.position();
        this.lockContentHeight();
        return true;
    }

    /** Ergänzt Tooltip-Semantik an vorhandenen und später gerenderten Auslösern */
    private initTriggers(): void {
        const prepareTrigger = (trigger: HTMLElement): void => {
            trigger.tabIndex = -1;
            trigger.setAttribute('role', 'button');
            trigger.setAttribute('aria-haspopup', 'dialog');
            trigger.setAttribute('aria-controls', this.tooltip.id);
            trigger.setAttribute('aria-expanded', 'false');
        };

        const prepareTriggers = (root: ParentNode): void => {
            root.querySelectorAll<HTMLElement>(this.optionen.triggerSelektor)
                .forEach(prepareTrigger);
        };

        prepareTriggers(document);

        const observer = new MutationObserver(mutations => {
            mutations.forEach(mutation => {
                if (mutation.type === 'attributes' && mutation.target instanceof HTMLElement && mutation.target.matches(this.optionen.triggerSelektor)) {
                    prepareTrigger(mutation.target);
                    return;
                }

                mutation.addedNodes.forEach(node => {
                    if (!(node instanceof HTMLElement))
                        return;

                    if (node.matches(this.optionen.triggerSelektor))
                        prepareTrigger(node);
                    prepareTriggers(node);
                });
            });
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: [...this.optionen.triggerAttribute]
        });
    }

    /** Öffnet oder schließt ein Popover nach Klick beziehungsweise Tastaturaktivierung */
    private toggle(trigger: HTMLElement, focusContent: boolean = false): void {
        if (trigger === this.activeTrigger && this.tooltip.hidden === false) {
            this.close();
            return;
        }

        if (this.show(trigger) === false)
            return;
        trigger.setAttribute('aria-expanded', 'true');

        if (focusContent)
            this.tooltip.querySelector<HTMLElement>('summary, a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])')?.focus();
    }

    /** Fixiert die vollständig aufgeklappte Inhaltshöhe bis zum nächsten Auslöser oder Resize */
    private lockContentHeight(): void {
        if (this.tooltip.hidden || this.prepareAufgabenbild() === false)
            return;

        this.content.style.height = '';
        this.content.style.height = `${this.content.offsetHeight}px`;
        this.position();
    }

    /** Verkleinert annähernd quadratische Aufgabenbilder, sobald ihre Maße bekannt sind */
    private prepareAufgabenbild(bild: HTMLImageElement | null = this.content.querySelector<HTMLImageElement>('.tooltip-aufgabenansicht-bild')): boolean {
        if (bild === null)
            return true;
        if (bild.complete === false)
            return false;

        if (bild.naturalHeight > 0)
            bild.classList.toggle('tooltip-aufgabenansicht-bild-kompakt', bild.naturalWidth / bild.naturalHeight < KOMPAKTES_BILD_SEITENVERHAELTNIS);
        return true;
    }

    /** Positioniert das Popover am Auslöser und hält es innerhalb des sichtbaren Browserbereichs */
    private position(): void {
        if (this.activeTrigger === null || this.tooltip.hidden)
            return;

        const margin = 12;
        const gap = 10;
        const trigger = this.activeTrigger;
        const triggerRect = trigger.getBoundingClientRect();
        const isTriggerVisible = trigger.isConnected && triggerRect.bottom > 0 && triggerRect.top < window.innerHeight && triggerRect.right > 0 && triggerRect.left < window.innerWidth;

        if (isTriggerVisible === false) {
            const hasTooltipFocus = this.tooltip.contains(document.activeElement);
            this.close();

            if (hasTooltipFocus && trigger.isConnected)
                trigger.focus({preventScroll: true});
            return;
        }

        const tooltipRect = this.tooltip.getBoundingClientRect();
        const availableAbove = triggerRect.top - margin;
        const availableBelow = window.innerHeight - triggerRect.bottom - margin;
        const showAbove = availableAbove >= tooltipRect.height || availableAbove > availableBelow;
        const preferredTop = showAbove ? triggerRect.top - tooltipRect.height - gap : triggerRect.bottom + gap;
        const top = Math.max(margin, Math.min(preferredTop, window.innerHeight - tooltipRect.height - margin));
        const preferredLeft = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
        const left = Math.max(margin, Math.min(preferredLeft, window.innerWidth - tooltipRect.width - margin));
        const arrowLeft = Math.max(14, Math.min(triggerRect.left + triggerRect.width / 2 - left, tooltipRect.width - 14));

        this.tooltip.dataset.position = showAbove ? 'top' : 'bottom';
        this.tooltip.style.setProperty('--tooltip-arrow-left', `${arrowLeft}px`);
        this.tooltip.style.top = `${top}px`;
        this.tooltip.style.left = `${left}px`;
    }

    /** Schließt das Popover und löst es vom bisherigen Auslöser */
    private close(): void {
        this.activeTrigger?.setAttribute('aria-expanded', 'false');
        this.tooltip.hidden = true;
        this.activeTrigger = null;
    }
}
